import React, { useEffect, useState } from 'react';
import { ContactUsHeroProps } from '../training/training-benefits/Types';
import CustomHeader from '../training/CustomHeader';
import {
  getHeaderLevel,
  getTextColorClass,
  getTextAlignmentClass,
} from '../../services/generalUtils';
import ContactUsBannerWithAgilityData from './ContactUsBannerWithAgilityData';

const hexToRgba = (color: string, opacity: number): string => {
  if (color.startsWith('#')) {
    let hex = color.replace('#', '');
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
};

const getDesktopHeaderStyles = (
  level: string | undefined,
): { fontSize: string; lineHeight: string } => {
  const headingLevelMap: Record<
    string,
    { fontSize: string; lineHeight: string }
  > = {
    h1: { fontSize: '48px', lineHeight: '54px' },
    h2: { fontSize: '40px', lineHeight: '46px' },
    h3: { fontSize: '36px', lineHeight: '42px' },
    h4: { fontSize: '32px', lineHeight: '38px' },
    h5: { fontSize: '28px', lineHeight: '32px' },
    h6: { fontSize: '24px', lineHeight: '30px' },
  };

  const normalizedLevel = level?.toLowerCase() || 'h2';
  return headingLevelMap[normalizedLevel] || headingLevelMap.h2;
};

const ContactUsHero = ({ module }: { module: any }) => {
  const fields = module?.fields || ({} as ContactUsHeroProps);

  const {
    headingText,
    backgroundImage,
    overlayEnabled,
    overlayColor,
    blurBoxColor,
    headingStyle,
  } = fields;

  const [isMobile, setIsMobile] = useState(false);
  const [isMediumScreen, setIsMediumScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsMediumScreen(width >= 768 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const gradientOverlayStyle = overlayColor
    ? `linear-gradient(179deg, ${hexToRgba(overlayColor, 0.6)} 33.33%, ${hexToRgba(overlayColor, 0.95)} 99.95%)`
    : 'linear-gradient(90.06deg, rgba(0, 51, 77, 0.5) 66.33%, rgba(0, 119, 179, 0.5) 99.95%)';

  const baseStyle = {
    paddingTop: '10px',
    minHeight: '350px',
    ...(isMobile && { paddingTop: '30px' }),
  };

  const backgroundStyle = {
    ...baseStyle,
    ...(backgroundImage?.url && {
      backgroundImage:
        String(overlayEnabled).toLowerCase() === 'true'
          ? `${gradientOverlayStyle}, url(${backgroundImage.url})`
          : `url(${backgroundImage.url})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundBlendMode: 'multiply',
    }),
  };

  const formStyle = {
    backgroundColor:
      blurBoxColor && blurBoxColor !== 'none'
        ? hexToRgba(blurBoxColor, 0.8)
        : 'rgba(0, 0, 0, 0.8)',
    width: isMobile ? '90%' : '40%',
    padding: 0,
    borderRadius: '8px',
    margin: '0 auto',
    marginBottom: '20px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box' as const,
    paddingTop: baseStyle.paddingTop,
  };

  const formContentStyle = {
    width: '100%',
    paddingTop: isMobile ? '20px' : '20px',
    paddingRight: isMobile ? '20px' : '35px',
    paddingLeft: isMobile ? '20px' : '35px',
    paddingBottom: isMobile ? '5px' : '5px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0px',
    boxSizing: 'border-box' as const,
  };

  return (
    <div
      style={backgroundStyle}
      className={`w-full min-h-screen flex flex-col items-center ${!isMobile ? 'justify-center' : 'pt-2'}`}
    >
      <div style={formStyle}>
        <div style={formContentStyle}>
          <CustomHeader
            text={headingText}
            data-testid="form-header"
            headerStyle={{
              level: getHeaderLevel(headingStyle?.fields?.level),
              color: getTextColorClass(headingStyle?.fields.color) || '#FFF',
              alignment:
                getTextAlignmentClass(headingStyle?.fields?.alignment) ||
                'text-center',
              fontWeight: 600,
              fontSize: isMobile
                ? '30px'
                : isMediumScreen
                  ? '35px'
                  : getDesktopHeaderStyles(headingStyle?.fields?.level)
                      .fontSize,
              lineHeight: isMobile
                ? '25px'
                : isMediumScreen
                  ? '20px'
                  : getDesktopHeaderStyles(headingStyle?.fields?.level)
                      .lineHeight,
              letterSpacing: '0%',
              fontFamily: 'Source Sans 3',
              className: `${getTextAlignmentClass(headingStyle?.fields.alignment)}`,
            }}
          />
          {/* Contact Banner - Fetches its own Agility data */}
          <ContactUsBannerWithAgilityData
            agilityContentListName="contact-form-options"
            compact
          />
        </div>
      </div>
    </div>
  );
};

export default ContactUsHero;
